/**
 * Thin policy wrapper around Sonner's `toast`. Most surfaces import this in place
 * of `sonner` so existing generic calls (.success / .error / .info / callable
 * default) pass straight through unchanged, while a couple of platform-aware
 * helpers let us suppress noisy confirmations on native without forking pages.
 *
 * Locked policy (docs/NATIVE_POLISH_PLAN.md):
 *   - location-detect status toasts: suppressed on ALL platforms (the branch
 *     dropdown + ChooseStoreHero are the real feedback).
 *   - "Added to cart" success toasts: suppressed on NATIVE (the CartDrawer pops
 *     to confirm), kept on WEB.
 *   - ALL error toasts: kept everywhere.
 */
import { toast as sonnerToast } from 'sonner';
import { isNativeApp } from './nativePayment';

/**
 * Policy-aware toast. Callable like `toast(msg, opts)` and carries the same
 * `.success/.error/.info/.loading/.warning/.dismiss` surface as Sonner (all
 * passthrough), plus `.cartAdd` and `.locationStatus` policy helpers.
 *
 * @param {*} args - forwarded verbatim to `sonnerToast`.
 * @returns {*} whatever Sonner returns.
 */
export const toast = (...args) => sonnerToast(...args);

/** Passthrough — success toast, unchanged. */
toast.success = (...args) => sonnerToast.success(...args);
/** Passthrough — error toast. ALWAYS shown (never gated). */
toast.error = (...args) => sonnerToast.error(...args);
/** Passthrough — info toast, unchanged. */
toast.info = (...args) => sonnerToast.info(...args);
/** Passthrough — loading toast, unchanged. */
toast.loading = (...args) => sonnerToast.loading(...args);
/** Passthrough — warning toast, unchanged. */
toast.warning = (...args) => sonnerToast.warning(...args);
/** Passthrough — dismiss a toast (or all), unchanged. */
toast.dismiss = (...args) => sonnerToast.dismiss(...args);

/**
 * Add-to-cart confirmation. No-op on native (the CartDrawer already pops to
 * confirm the add); a normal success toast on web.
 *
 * @param {string} msg - confirmation message.
 * @param {object} [opts] - Sonner toast options.
 * @returns {*} the Sonner toast id on web, undefined on native.
 */
toast.cartAdd = (msg, opts) => {
  if (isNativeApp()) return undefined;
  return sonnerToast.success(msg, opts);
};

/**
 * Location-detection status. No-op on ALL platforms — the branch dropdown and
 * ChooseStoreHero are the real feedback surfaces, so these status toasts are
 * pure noise. Error toasts (e.g. "Location not available") still go through
 * `toast.error` and are unaffected.
 *
 * @param {string} _msg - status message (ignored).
 * @param {object} [_opts] - Sonner toast options (ignored).
 * @returns {undefined}
 */
// eslint-disable-next-line no-unused-vars
toast.locationStatus = (_msg, _opts) => undefined;
